import { Text } from "@mantine/core";
import React, { useEffect, useMemo, useRef, useState } from "react";

const asDate = (d: Date | string | null | undefined): Date | null => {
	if (!d) return null;
	if (d instanceof Date) return isNaN(+d) ? null : d;
	const iso = /Z$/.test(d) ? d : `${d}Z`;
	const parsed = new Date(iso);
	return isNaN(+parsed) ? null : parsed;
};

type DueCountdownProps = {
	targetDate: Date | string | null | undefined;
	activeTimer?: boolean;
    textSz?: number;
    pastDueText?: string;
};

export const Countdown: React.FC<DueCountdownProps> = ({ targetDate, activeTimer, textSz, pastDueText }) => {
	const target = useMemo(() => asDate(targetDate), [targetDate]);
	const [now, setNow] = useState<Date>(() => new Date());
	const timerRef = useRef<number | null>(null);

	const { countdownText, nextUpdateMs, isPast } = useMemo(() => {
		if (!target) {
			return {
				countdownText: "—",
				nextUpdateMs: 0,
				isPast: false,
			};
		}

		const diffMs = target.getTime() - now.getTime();
		if (diffMs <= 0) {
			return {
				countdownText: pastDueText || "Past due",
				nextUpdateMs: 0,
				isPast: true,
			};
		}

		// active HH:MM:SS mode
		if (activeTimer) {
			const totalSeconds = Math.floor(diffMs / 1000);
			const hours = Math.floor(totalSeconds / 3600);
			const minutes = Math.floor((totalSeconds % 3600) / 60);
			const seconds = totalSeconds % 60;
			const pad = (n: number) => n.toString().padStart(2, "0");

			return {
				countdownText: `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`,
				nextUpdateMs: 1000, // always every second
				isPast: false,
			};
		}

		// normal mode
		const seconds = Math.floor(diffMs / 1000);
		const minutes = Math.floor(seconds / 60);
		const hours = Math.floor(minutes / 60);
		const days = Math.floor(hours / 24);

		let nextUpdateMs = 1000;
		let countdownText = "";

		if (days >= 1) {
			countdownText = `${days} day${days === 1 ? "" : "s"} left`;
			nextUpdateMs = 24 * 60 * 60 * 1000;
		} else if (hours >= 1) {
			countdownText = `${hours} hour${hours === 1 ? "" : "s"} left`;
			nextUpdateMs = 60 * 60 * 1000;
		} else if (minutes >= 1) {
			countdownText = `${minutes} minute${minutes === 1 ? "" : "s"} left`;
			nextUpdateMs = 60 * 1000;
		} else {
			const secs = Math.max(1, seconds);
			countdownText = `${secs} second${secs === 1 ? "" : "s"} left`;
			nextUpdateMs = 1000;
		}

		return {
			countdownText,
			nextUpdateMs,
			isPast: false,
		};
	}, [target, now, activeTimer]); // <-- depends on `now` too

	useEffect(() => {
		// clear previous
		if (timerRef.current) {
			window.clearTimeout(timerRef.current);
			timerRef.current = null;
		}

		if (!target || nextUpdateMs <= 0) return;

		const id = window.setTimeout(() => {
			setNow(new Date());
		}, nextUpdateMs);

		timerRef.current = id;

		return () => {
			if (timerRef.current) {
				window.clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		};
	}, [target, nextUpdateMs, now]); // <-- this is the key change

	const color = isPast ? "red" : "inherit";

	return <Text c={color} fz={textSz}>{countdownText}</Text>;
};